/**
 * Wizard Step 2 — Apartments (COL-252).
 *
 * Each apartment is configured independently: its owner (owner = lessor by
 * default), revenue model + fees, and its rooms. There is no global Lessor or
 * Revenue step — everything lives per apartment here. The commercial terms
 * (collection mode, revenue model, fee) are STAFF-ONLY: an owner submitting
 * their own building does not propose how CoLiviq earns from it — CoLiviq sets
 * that on approval — so `ownerSubmitting` hides the whole block.
 *
 * The apartment list
 * itself is seeded from, and kept in sync with, Step 1's apartment count by
 * `useApartmentCountSync` (called from the always-mounted wizard page, not
 * from here — this step is only rendered while active and remounts on every
 * visit, see COL-379).
 */
import { useRef, useState } from "react";
import { Plus, X, Building2, BedDouble, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BilingualTextarea } from "@/components/admin/v2/BilingualTextarea";
import { LessorCombobox } from "@/components/admin/v2/LessorCombobox";
import { LessorFormDialog } from "@/components/admin/v2/LessorFormDialog";
import { MAX_ROOM_FILL } from "@/components/admin/v2/ApartmentEditSheet";
import { useCreateLessor, useLessor } from "@/hooks/v2/useLessors";
import { useToast } from "@/hooks/use-toast";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import type { LessorInput } from "@/types/lessor";
import {
  COLLECTION_MODES,
  REVENUE_MODELS,
  type CollectionMode,
  type RevenueModel,
} from "@/types/property";
import {
  newApartment,
  newRoom,
  useWizard,
  type WizardApartment,
  type WizardRoom,
  type WizardState,
} from "./wizardContext";
import { useAuth } from "@/contexts/AuthContext";
import { collectionModeLabels } from "@/lib/collection-mode";
import { REVENUE_MODEL_LABELS } from "@/lib/revenue-model";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { useLanguage } from "@/contexts/LanguageContext";

/** Sentinel for "no apartment-level override — follow the property".
 *  Mirrors ApartmentEditSheet's identical INHERIT convention for the same
 *  concept on an apartment added to an existing property. */
const INHERIT = "__inherit__";

/** Rename any room whose title still matches "{oldLabel} — Room {n}" to
 *  "{newLabel} — Room {n}" — by the room's own embedded number, not its
 *  array position (deleting an earlier room reindexes the rest without
 *  renumbering their titles, so position and embedded number can diverge).
 *  A room renamed away from that pattern has been customized directly and
 *  is left alone. Shared by the label-edit cascade (ApartmentCard.setLabel)
 *  and the apartment-removal resync below, which both need the same rule. */
function renameGeneratedRoomTitles(
  rooms: WizardRoom[],
  oldLabel: string,
  newLabel: string
): WizardRoom[] {
  if (oldLabel === newLabel) return rooms;
  return rooms.map((r) => {
    const match = /^(.*) — Room (\d+)$/.exec(r.title);
    if (!match || match[1] !== oldLabel) return r;
    return { ...r, title: `${newLabel} — Room ${match[2]}` };
  });
}

export function Step2Apartments() {
  const { state, setApartments } = useWizard();
  const { isOwnerPortalOnly } = useAuth();
  /* Operators run this wizard under their own brand (COL-352 Phase 5); staff
     see the generic "coliviq" wording. */
  const { platformName, isOperator } = useOperatorBranding();
  const { t } = useLanguage();
  const COLLECTION_MODE_LABELS = collectionModeLabels(null, platformName, t);
  const apartments = state.apartments;
  // The inline "Add owner" dialog applies its patch from a useMutation
  // onSuccess — asynchronous, unlike every other edit in this file. If the
  // admin edits a DIFFERENT apartment (or adds/removes one) while that
  // create-lessor request is still in flight, patchApt closing over the
  // `apartments` variable from the render at submit-time would overwrite
  // those intervening edits once it resolves. Reading the array through a
  // ref kept in sync on every render instead means the patch always applies
  // on top of the latest committed state, however long the request takes.
  const apartmentsRef = useRef(apartments);
  apartmentsRef.current = apartments;

  function patchApt(localId: string, patch: Partial<WizardApartment>) {
    setApartments(
      apartmentsRef.current.map((a) =>
        a.localId === localId ? { ...a, ...patch } : a
      )
    );
  }
  function addApt() {
    setApartments([
      ...apartments,
      newApartment(`Apartment ${apartments.length + 1}`),
    ]);
  }
  function removeApt(localId: string) {
    const remaining = apartments.filter((a) => a.localId !== localId);
    // An apartment whose label was never typed into still shows (and
    // embeds, in its own auto-generated room titles) a position-based
    // fallback name like "Apartment 2". Removing an EARLIER apartment
    // shifts everyone after it to a new index/fallback name — resync those
    // room titles here, at the moment the shift happens, so a later real
    // label edit's cascade (which matches by exact old-label string) still
    // recognizes them as auto-generated instead of wrongly treating a
    // since-stale index as a customization.
    const resynced = remaining.map((a, newIndex) => {
      if (a.label.trim()) return a; // has a real label — index doesn't matter
      const oldIndex = apartments.findIndex((x) => x.localId === a.localId);
      if (oldIndex === newIndex) return a;
      return {
        ...a,
        rooms: renameGeneratedRoomTitles(
          a.rooms,
          `Apartment ${oldIndex + 1}`,
          `Apartment ${newIndex + 1}`
        ),
      };
    });
    setApartments(resynced);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {isOwnerPortalOnly
          ? t(
              "admin.wizard.step2.ownerIntro",
              "Set up each apartment and its rooms. coliviq sets the commercial terms when we approve your submission."
            )
          : t(
              "admin.wizard.step2.staffIntro",
              "Configure each apartment: its owner, how {earns} from it, and its rooms."
            ).replace(
              "{earns}",
              isOperator
                ? t("admin.wizard.step2.youEarn", "you earn")
                : `${platformName} ${t("admin.wizard.step2.platformEarns", "earns")}`
            )}
      </p>

      {apartments.map((apt, i) => (
        <ApartmentCard
          key={apt.localId}
          index={i}
          apt={apt}
          property={state.property}
          canRemove={apartments.length > 1}
          ownerSubmitting={isOwnerPortalOnly}
          collectionModeLabels={COLLECTION_MODE_LABELS}
          onPatch={(patch) => patchApt(apt.localId, patch)}
          onRemove={() => removeApt(apt.localId)}
        />
      ))}

      <Button type="button" variant="outline" onClick={addApt}>
        <Plus className="mr-2 h-4 w-4" />
        {t("admin.wizard.step2.addApartment", "Add apartment")}
      </Button>
    </div>
  );
}

function ApartmentCard({
  index,
  apt,
  property,
  canRemove,
  ownerSubmitting,
  collectionModeLabels: COLLECTION_MODE_LABELS,
  onPatch,
  onRemove,
}: {
  index: number;
  apt: WizardApartment;
  /** Source of the "Inherit from property (...)" option's label. */
  property: WizardState["property"];
  canRemove: boolean;
  /** The submitter IS the owner: hides the owner picker, the representor
   *  fields and the commercial terms — none of which are theirs to set. */
  ownerSubmitting?: boolean;
  /** Resolved once by the parent rather than per card. */
  collectionModeLabels: Record<CollectionMode, string>;
  onPatch: (patch: Partial<WizardApartment>) => void;
  onRemove: () => void;
}) {
  const { platformName } = useOperatorBranding();
  const { t } = useLanguage();
  const { toast } = useToast();
  // What the contract would use if this apartment sets no override.
  const {
    data: selectedOwner,
    isLoading: selectedOwnerLoading,
    isError: selectedOwnerErrored,
  } = useLessor(apt.lessorId);
  const inheritedRepName = selectedOwner?.rep_name?.trim() ?? "";
  const inheritedRepNote = selectedOwner?.rep_note?.trim() ?? "";

  const aptLabel = apt.label.trim() || `Apartment ${index + 1}`;

  const [createOwnerOpen, setCreateOwnerOpen] = useState(false);
  const createLessorMut = useCreateLessor();
  function handleCreateOwner(input: LessorInput) {
    createLessorMut.mutate(input, {
      onSuccess: (lessor) => {
        toast({
          title: t("admin.wizard.step2.ownerAdded", "Owner added"),
          description: lessor.name,
        });
        onPatch({ lessorId: lessor.id, ownerUserId: null });
        setCreateOwnerOpen(false);
      },
      onError: (err) =>
        toast({
          title: t(
            "admin.wizard.step2.couldNotAddOwner",
            "Could not add owner"
          ),
          description: formatSupabaseError(err),
          variant: "destructive",
        }),
    });
  }

  function setRooms(rooms: WizardRoom[]) {
    onPatch({ rooms });
  }
  /** Renaming the apartment (or its placeholder fallback) live-renames any
   *  room whose title still matches the auto-generated "{label} — Room {n}"
   *  pattern for the OLD label — a room renamed away from that pattern has
   *  been customized directly and keeps its own name. */
  function setLabel(rawLabel: string) {
    const oldLabel = aptLabel;
    const newLabel = rawLabel.trim() || `Apartment ${index + 1}`;
    onPatch({
      label: rawLabel,
      rooms: renameGeneratedRoomTitles(apt.rooms, oldLabel, newLabel),
    });
  }
  /** Same idea for floor: a room still carrying the apartment's OLD floor
   *  value (auto-filled or coincidentally matching) follows the change; a
   *  room set to something else keeps it. */
  function setFloor(newFloor: string) {
    const oldFloor = apt.floor;
    const rooms = apt.rooms.map((r) =>
      r.floor === oldFloor ? { ...r, floor: newFloor } : r
    );
    onPatch({ floor: newFloor, rooms });
  }
  /** Prefill for a freshly-created room: apartment's floor, and the first
   *  room's description (COL-252 wizard room auto-fill — same idea as
   *  ApartmentEditSheet's auto-named room fill, extended to floor + desc). */
  function roomSeed(roomNumber: number): Partial<WizardRoom> {
    const first = apt.rooms[0];
    return {
      title: `${aptLabel} — Room ${roomNumber}`,
      floor: apt.floor,
      descriptionEn: first?.descriptionEn ?? "",
      descriptionDe: first?.descriptionDe ?? "",
    };
  }
  function patchRoom(localId: string, patch: Partial<WizardRoom>) {
    const isFirstRoom = apt.rooms[0]?.localId === localId;
    const isDescPatch = "descriptionEn" in patch || "descriptionDe" in patch;
    const prevFirst = apt.rooms[0];
    let rooms = apt.rooms.map((r) =>
      r.localId === localId ? { ...r, ...patch } : r
    );
    // The first room's description carries over to sibling rooms that are
    // still "following" it — blank, or still equal to what room 1 held
    // before this edit. A sibling stops following the moment someone types
    // something else into it directly, but otherwise keeps tracking room 1
    // through further edits (not just a one-time copy at creation).
    if (isFirstRoom && isDescPatch) {
      const nextFirst = rooms[0];
      rooms = rooms.map((r, i) => {
        if (i === 0) return r;
        const next = { ...r };
        if ("descriptionEn" in patch) {
          const wasFollowing =
            !r.descriptionEn.trim() ||
            r.descriptionEn === prevFirst.descriptionEn;
          if (wasFollowing) next.descriptionEn = nextFirst.descriptionEn;
        }
        if ("descriptionDe" in patch) {
          const wasFollowing =
            !r.descriptionDe.trim() ||
            r.descriptionDe === prevFirst.descriptionDe;
          if (wasFollowing) next.descriptionDe = nextFirst.descriptionDe;
        }
        return next;
      });
    }
    setRooms(rooms);
  }
  function addRoom() {
    const rooms = [
      ...apt.rooms,
      { ...newRoom(), ...roomSeed(apt.rooms.length + 1) },
    ];
    setRooms(rooms);
    onPatch({ rooms, numRooms: String(rooms.length) });
  }
  function removeRoom(localId: string) {
    const rooms = apt.rooms.filter((r) => r.localId !== localId);
    onPatch({ rooms, numRooms: String(rooms.length) });
  }
  /** Resizes the room list to match a typed "Number of rooms" — the
   *  auto-create/remove behavior requested for the wizard, mirroring how
   *  ApartmentEditSheet auto-fills rooms up to a count on an existing
   *  property, but live and two-way here since nothing is persisted yet.
   *  Clamped to MAX_ROOM_FILL, same cap ApartmentEditSheet enforces on save —
   *  a typo'd extra zero shouldn't synchronously allocate thousands of room
   *  objects (and later, one insert per room on submit). The displayed count
   *  is clamped along with the room list itself, so the field never shows a
   *  number the room list doesn't actually match. */
  function setNumRooms(raw: string) {
    const parsed = Math.max(0, Math.floor(Number(raw) || 0));
    const n = Math.min(MAX_ROOM_FILL, parsed);
    const display = raw.trim() === "" ? raw : String(n);
    let rooms = apt.rooms;
    if (raw.trim() !== "" && n !== rooms.length) {
      rooms =
        n > rooms.length
          ? [
              ...rooms,
              ...Array.from({ length: n - rooms.length }).map((_, i) => ({
                ...newRoom(),
                ...roomSeed(rooms.length + i + 1),
              })),
            ]
          : rooms.slice(0, n);
    }
    onPatch({ numRooms: display, rooms });
  }

  return (
    <Card className="p-5 space-y-4">
      {/* Apartment basics */}
      <div className="flex items-start gap-3">
        <Building2 className="mt-1 h-5 w-5 text-muted-foreground shrink-0" />
        <div className="grid flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <WField
            label={`${t("admin.wizard.step2.apartmentWord", "Apartment")} ${index + 1} ${t("admin.wizard.step2.labelWord", "label")}`}
          >
            <Input
              value={apt.label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`${t("admin.wizard.step2.apartmentWord", "Apartment")} ${index + 1}`}
              className="h-9"
            />
          </WField>
          <WField label={t("admin.wizard.step2.unitFloor", "Unit / Floor")}>
            <Input
              value={apt.floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder={t(
                "admin.wizard.step2.floorPlaceholderExample",
                "e.g. EG Links"
              )}
              className="h-9"
            />
          </WField>
          <WField label={t("admin.wizard.step2.sizeSqm", "Size (m²)")}>
            <Input
              type="number"
              min={0}
              step="0.1"
              inputMode="decimal"
              value={apt.size_sqm}
              onChange={(e) => onPatch({ size_sqm: e.target.value })}
              placeholder="72"
              className="h-9 tabular-nums"
            />
          </WField>
          <WField
            label={t("admin.wizard.step2.numberOfRooms", "Number of rooms")}
          >
            <Input
              type="number"
              min={0}
              max={MAX_ROOM_FILL}
              step="1"
              inputMode="numeric"
              value={apt.numRooms}
              onChange={(e) => setNumRooms(e.target.value)}
              placeholder="3"
              className="h-9 tabular-nums"
            />
          </WField>
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label={`${t("admin.wizard.step2.removeWord", "Remove")} ${apt.label || `${t("admin.wizard.step2.apartmentWordLower", "apartment")} ${index + 1}`}`}
            className="h-8 w-8 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Owner picker — admin/staff only. An owner submitting their own
          property IS the owner; there's nothing to pick. */}
      {!ownerSubmitting && (
        <div className="grid gap-3 sm:grid-cols-2">
          <WField
            label={t("admin.wizard.step2.owner", "Owner")}
            className="sm:col-span-2"
          >
            <div className="flex items-center gap-2">
              <LessorCombobox
                className="flex-1 min-w-0"
                value={apt.lessorId}
                onChange={(next) =>
                  onPatch({ lessorId: next, ownerUserId: null })
                }
                ownerValue={apt.ownerUserId}
                // Keep the portal link AND write the owner's lessor when they have
                // one — lessor_id is what the Owners surfaces and the invoice
                // generator bill by (same rule as ApartmentEditSheet).
                onSelectOwner={(userId, owner) =>
                  onPatch({
                    ownerUserId: userId,
                    lessorId: owner.lessor_id ?? null,
                  })
                }
                placeholder={t(
                  "admin.wizard.step2.selectOwner",
                  "Select owner…"
                )}
                noneLabel={t(
                  "admin.wizard.step2.noOwnerLinked",
                  "No owner linked"
                )}
              />
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => setCreateOwnerOpen(true)}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {t("admin.wizard.step2.addOwner", "Add owner")}
              </Button>
            </div>
            {apt.ownerUserId && !apt.lessorId && (
              // No lessor behind the account → nothing downstream (Owners list,
              // invoices) can attribute the apartment, since they key on lessor.
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-600">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>
                  {t(
                    "admin.wizard.step2.noOwnerRecordWarning",
                    "This owner account has no owner record — the apartment won't appear under Owners or be invoiced until one is linked."
                  )}
                </span>
              </p>
            )}
          </WField>

          {/* Representor — prints after "vertreten durch" on this apartment's
              contracts. Blank inherits the owner's default, shown as the
              placeholder so it's visible without having to type anything.
              Admin-only alongside the owner picker: owner_submit_property
              doesn't carry rep_override, so a self-submitter would type a name
              here and lose it on save. */}
          <WField
            label={t("admin.wizard.step2.representor", "Representor")}
            required
            className="sm:col-span-2"
          >
            <Input
              className="h-9"
              value={apt.repName}
              onChange={(e) => onPatch({ repName: e.target.value })}
              placeholder={
                inheritedRepName ||
                t(
                  "admin.wizard.step2.repNamePlaceholderExample",
                  'e.g. "den Geschäftsführer" or "Coliviq GmbH"'
                )
              }
            />
            {apt.repName.trim() ? (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t(
                  "admin.wizard.step2.repOverridesOwner",
                  "Overrides the owner's default for this apartment only."
                )}
              </p>
            ) : inheritedRepName ? (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t(
                  "admin.wizard.step2.repInheritedFromOwner",
                  "Inherited from the owner: {name}. Leave blank to keep it."
                ).replace("{name}", inheritedRepName)}
              </p>
            ) : apt.lessorId &&
              (selectedOwnerLoading || selectedOwnerErrored) ? (
              // An owner is linked but its data hasn't loaded yet (or the
              // lookup failed) — don't flash the "required" error for a
              // representor we simply couldn't read; Next/Submit are gated on
              // PropertyNew's own lessor list, not this query.
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t(
                  "admin.wizard.step2.repLeaveBlank",
                  "Leave blank to use the owner's default."
                )}
              </p>
            ) : (
              // No override, and either no linked owner or its default
              // representor resolved blank — the wizard blocks Next/Submit on
              // this (see wizardValidation.ts's lessorRepNames-aware check),
              // so flag it here rather than leaving a silent blocker.
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>
                  {t(
                    "admin.wizard.step2.repRequiredHint",
                    "Required. Enter a representor for this apartment, or link an owner whose default representor is set."
                  )}
                </span>
              </p>
            )}
          </WField>
          <WField
            label={t("admin.wizard.step2.representorNote", "Representor note")}
            className="sm:col-span-2"
          >
            <Textarea
              rows={2}
              value={apt.repNote}
              onChange={(e) => onPatch({ repNote: e.target.value })}
              placeholder={
                inheritedRepNote ||
                t(
                  "admin.wizard.step2.repNotePlaceholder",
                  "Optional. Internal note about who signs for this apartment."
                )
              }
            />
            {/* A note with no name isn't stored — see the rep_override payload
                in useCreatePropertyV2. Say so rather than dropping it
                silently. */}
            {apt.repNote.trim() && !apt.repName.trim() && (
              <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-500">
                {t(
                  "admin.wizard.step2.addRepresentorWarning",
                  "Add a representor above for this note to be saved."
                )}
              </p>
            )}
          </WField>
        </div>
      )}

      {/* Revenue / collection — STAFF ONLY. Owners submitting their own
          building used to propose these terms here for coliviq to confirm on
          approval. They no longer see them at all: a submission now arrives
          with the columns NULL (= inherit, see apartments.collection_mode)
          and staff set them on the apartment once it is live. */}
      {!ownerSubmitting && (
        <div className="grid gap-3 sm:grid-cols-2">
          <WField
            label={t("admin.wizard.step2.collectionMode", "Collection mode")}
            className="sm:col-span-2"
          >
            <Select
              value={apt.collectionMode ?? INHERIT}
              onValueChange={(v) =>
                onPatch({
                  collectionMode: v === INHERIT ? null : (v as CollectionMode),
                })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={INHERIT}>
                  {t(
                    "admin.wizard.step2.inheritFromProperty",
                    "Inherit from property"
                  )}{" "}
                  ({COLLECTION_MODE_LABELS[property.collectionMode]})
                </SelectItem>
                {COLLECTION_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {COLLECTION_MODE_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </WField>
          <WField label={t("admin.wizard.step2.revenueModel", "Revenue model")}>
            <Select
              value={apt.revenueModel ?? INHERIT}
              onValueChange={(v) =>
                onPatch({
                  revenueModel: v === INHERIT ? null : (v as RevenueModel),
                })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={INHERIT}>
                  {t("admin.wizard.step2.inherit", "Inherit")} (
                  {REVENUE_MODEL_LABELS[property.revenueModel]})
                </SelectItem>
                {REVENUE_MODELS.map((rm) => (
                  <SelectItem key={rm} value={rm}>
                    {REVENUE_MODEL_LABELS[rm]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </WField>
          {apt.revenueModel === "full_rent" && (
            <WField
              label={t("admin.wizard.step2.masterRent", "Master rent (€/mo)")}
            >
              <Input
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={apt.masterRent}
                onChange={(e) => onPatch({ masterRent: e.target.value })}
                className="h-9 tabular-nums"
              />
            </WField>
          )}
          {apt.revenueModel === "pct_of_rent" && (
            <WField
              label={`${platformName} ${t("admin.wizard.step2.feePercentSuffix", "fee (%)")}`}
            >
              <Input
                type="number"
                min={0}
                max={100}
                step="0.1"
                inputMode="decimal"
                value={apt.coliviqFeePct}
                onChange={(e) => onPatch({ coliviqFeePct: e.target.value })}
                placeholder="25"
                className="h-9 tabular-nums"
              />
            </WField>
          )}
          {apt.revenueModel === "flat_fee_per_room" && (
            <WField
              label={t(
                "admin.wizard.step2.flatFeePerRoom",
                "Flat fee (€ / room)"
              )}
            >
              <Input
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={apt.coliviqFeeAmount}
                onChange={(e) => onPatch({ coliviqFeeAmount: e.target.value })}
                placeholder="120"
                className="h-9 tabular-nums"
              />
            </WField>
          )}
        </div>
      )}

      {/* Nested rooms */}
      <div className="rounded-lg border bg-muted/20">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <BedDouble className="h-3.5 w-3.5" />
            {t("admin.wizard.step2.roomsCount", "Rooms ({n})").replace(
              "{n}",
              String(apt.rooms.length)
            )}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={addRoom}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t("admin.wizard.step2.addRoom", "Add room")}
          </Button>
        </div>
        {apt.rooms.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-muted-foreground">
            {t(
              "admin.wizard.step2.noRoomsYet",
              "No rooms yet. Add the rooms that belong to this apartment."
            )}
          </p>
        ) : (
          <div className="divide-y">
            {apt.rooms.map((r, ri) => (
              <div key={r.localId} className="px-3 py-2 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_100px_100px_auto] gap-2 items-end">
                  <WField label={t("admin.wizard.step2.roomName", "Name")}>
                    <Input
                      value={r.title}
                      onChange={(e) =>
                        patchRoom(r.localId, { title: e.target.value })
                      }
                      placeholder={`${t("admin.wizard.step2.roomWord", "Room")} ${ri + 1}`}
                      className="h-8"
                    />
                  </WField>
                  <WField label={t("admin.wizard.step2.rentEur", "Rent (€)")}>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      inputMode="decimal"
                      value={r.price}
                      onChange={(e) =>
                        patchRoom(r.localId, { price: e.target.value })
                      }
                      className="h-8 tabular-nums"
                    />
                  </WField>
                  <WField label={t("admin.wizard.step2.sizeM2", "Size m²")}>
                    <Input
                      type="number"
                      min={0}
                      step="0.1"
                      inputMode="decimal"
                      value={r.size_sqm}
                      onChange={(e) =>
                        patchRoom(r.localId, { size_sqm: e.target.value })
                      }
                      className="h-8 tabular-nums"
                    />
                  </WField>
                  <WField label={t("admin.wizard.step2.floorWord", "Floor")}>
                    <Input
                      value={r.floor}
                      onChange={(e) =>
                        patchRoom(r.localId, { floor: e.target.value })
                      }
                      placeholder={t(
                        "admin.wizard.step2.floorPlaceholderOrdinal",
                        "2nd"
                      )}
                      className="h-8"
                    />
                  </WField>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRoom(r.localId)}
                    aria-label={`${t("admin.wizard.step2.removeWord", "Remove")} ${r.title || `${t("admin.wizard.step2.roomWordLower", "room")} ${ri + 1}`}`}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {/* Staff-only: an owner's submission goes through
                    owner_submit_property (a SECURITY DEFINER RPC), whose
                    payload/columns don't carry a room description — showing
                    the field to an owner would silently discard whatever
                    they typed. */}
                {!ownerSubmitting && (
                  <WField
                    label={t("admin.wizard.step2.description", "Description")}
                  >
                    <BilingualTextarea
                      valueEn={r.descriptionEn}
                      valueDe={r.descriptionDe}
                      onChange={({ en, de }) =>
                        patchRoom(r.localId, {
                          descriptionEn: en,
                          descriptionDe: de,
                        })
                      }
                      rows={2}
                      placeholder={t(
                        "admin.wizard.step2.roomDescPlaceholder",
                        "Highlights, view, furnishings…"
                      )}
                    />
                  </WField>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!ownerSubmitting && (
        <LessorFormDialog
          open={createOwnerOpen}
          // Block Escape/overlay dismissal while the request is pending, not
          // just the dialog's own (already-disabled) Cancel button: this step
          // unmounts on every wizard navigation (see the top comment), and a
          // per-mutate onSuccess/onError only fires while its owning
          // component is still mounted (TanStack Query's MutationObserver
          // gates on hasListeners()) — dismissing here would let the admin
          // navigate away and silently lose the lessor→apartment assignment
          // even though the lessor row itself still gets created.
          onOpenChange={(next) => {
            if (!next && createLessorMut.isPending) return;
            setCreateOwnerOpen(next);
          }}
          mode="create"
          isSubmitting={createLessorMut.isPending}
          onSubmit={handleCreateOwner}
        />
      )}
    </Card>
  );
}

function WField({
  label,
  required,
  className,
  children,
}: {
  label?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1 ${className ?? ""}`}>
      {label && (
        <Label className="text-[11px] font-medium text-muted-foreground">
          {label}
          {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      {children}
    </div>
  );
}
